import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import Drawer from 'react-modern-drawer';
import 'react-modern-drawer/dist/index.css';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useDrawerLayer } from '../../../components/drawer/drawerLayerStack';
import { CourseSidebarTabs } from './CourseSidebarTabs';
import './course-panel-sheet.css';

const SHEET_DURATION_MS = 320;

const prefersReducedMotion = () =>
    typeof window !== 'undefined'
    && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Las tabs del curso en móvil: hoja inferior en vertical y panel lateral en
 * horizontal, donde la altura es el recurso escaso. Comparte capa con el resto
 * de drawers, así que los formularios de tareas o recursos se abren encima.
 */
export const CoursePanelSheet = ({
    id = 'course-panel-sheet',
    open,
    onClose,
    isLandscape = false,
    tabs,
    activeTab,
    onTabChange,
    onlineCount = 0,
    liveCount = 0,
    showLiveOnClasses = false,
    children,
}) => {
    const closeRef = useRef(null);
    const { zIndex } = useDrawerLayer({ isOpen: open, onRequestClose: onClose });

    useEffect(() => {
        if (!open) return undefined;

        const timer = setTimeout(() => closeRef.current?.focus(), SHEET_DURATION_MS);
        return () => clearTimeout(timer);
    }, [open]);

    return createPortal(
        <Drawer
            open={open}
            onClose={onClose}
            direction={isLandscape ? 'right' : 'bottom'}
            size={isLandscape ? 'min(26rem, 82vw)' : '88dvh'}
            duration={prefersReducedMotion() ? 0 : SHEET_DURATION_MS}
            overlayOpacity={0.45}
            zIndex={zIndex}
            className={`course-panel-sheet__container ${isLandscape ? 'is-side' : 'is-bottom'}`}
        >
            <section
                id={id}
                className="course-panel-sheet"
                role="dialog"
                aria-modal="true"
                aria-label="Panel del curso"
            >
                <header className="course-panel-sheet__head">
                    {!isLandscape ? <span className="course-panel-sheet__grip" aria-hidden="true" /> : null}

                    <h2 className="course-panel-sheet__title">Panel del curso</h2>

                    <button
                        ref={closeRef}
                        type="button"
                        className="course-panel-sheet__close"
                        onClick={onClose}
                        aria-label="Cerrar panel del curso"
                    >
                        <XMarkIcon className="course-panel-sheet__close-icon" aria-hidden="true" />
                    </button>
                </header>

                <CourseSidebarTabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={onTabChange}
                    onlineCount={onlineCount}
                    liveCount={liveCount}
                    showLiveOnClasses={showLiveOnClasses}
                />

                <div className="course-panel-sheet__body">
                    {children}
                </div>
            </section>
        </Drawer>,
        document.body,
    );
};
